import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {CssBaseline, ThemeProvider} from "@mui/material";
import {createBrowserRouter, Outlet, RouterProvider,} from "react-router-dom";
import {ZyusetuHomePage} from "./pages/zyusetu/ZyusetuHomePage";
import theme from "@/theme";
import {Layout} from "@/Layout.tsx";
import {ZyusetuEditPage} from "@/pages/zyusetu/ZyusetuEditPage.tsx";
import {Auth0Provider} from "@auth0/auth0-react";
import {EnsureLoggedIn} from "@/hooks/EnsureLoggedIn.tsx";
import "@/plugin/pdfjs-plugin";
import "@/plugin/zod-plugin";
import "@/plugin/dayjs-plugin";
import {HighlightFieldProvider} from "@/hooks/HighlightFieldProvider.tsx";
import {RentNewZyusetuPage} from "@/pages/zyusetu/new/RentNewZyusetuPage.tsx";
import {UserDataProvider} from "@/hooks/UserDataProvider.tsx";
import {ZyusetuReviewListPage} from "@/pages/zyusetu/ZyusetuReviewListPage.tsx";
import {NotFoundPage} from "@/pages/NotFoundPage.tsx";
import {ZyusetuReviewPage} from "@/pages/zyusetu/ZyusetuReviewPage.tsx";
import {ToolsPage} from "@/pages/tools/ToolsPage";
import {AreaCalculatorPage} from "@/pages/zyusetu/tools/AreaCalculatorPage.tsx";
import {ManagementBylawExtractor} from "@/pages/zyusetu/tools/ManagementBylawExtractor.tsx";
import {ContactPage} from "@/pages/ContactPage.tsx";
import {UsagePage} from "@/pages/usage/UsagePage";
import {UserAdminPage} from "@/pages/admin/UserAdminPage.tsx";
import {ApprovalFlowListPage} from "@/pages/admin/ApprovalFLowListPage.tsx";
import {ApprovalFlowEditPage} from "@/pages/admin/approvalFlow/ApprovalFlowEditPage.tsx";
import {CreateOrganizationPage} from "@/pages/paradis/organization/CreateOrganizationPage";
import {CompleteOrganizationPage} from "@/pages/paradis/organization/CompleteOrganizationPage";
import {HelmetProvider} from "react-helmet-async";
import {LoginPage} from "@/pages/LoginPage.tsx";
import {Toaster} from "react-hot-toast";
import {SaleNewZyusetuPage} from "@/pages/zyusetu/new/SaleNewZyusetuPage.tsx";

const router = createBrowserRouter([
  {
    element: <>
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        gutter={8}
        containerClassName="toaster-wrapper"
        containerStyle={{}}
        toastOptions={{
          // Define default options
          className: "",
        }}
      />
      <Outlet />
    </>,
    children: [
      {
        path: "paradis/organization",
        children: [
          {
            path: "new",
            element: <CreateOrganizationPage/>,
          },
          {
            path: "complete",
            element: <CompleteOrganizationPage/>,
          },
        ],
      },
      {
        path: "login",
        element: <LoginPage/>
      },
      {
        element: <Layout/>,
        children: [
          {
            element: <EnsureLoggedIn/>,
            children: [
              {
                path: "",
                element: <ZyusetuHomePage/>,
              },
              {
                path: "contact",
                element: <ContactPage/>,
              },
              {
                path: "usage",
                element: <UsagePage/>,
              },
              {
                path: "admin",
                children: [
                  {
                    path: "",
                    element: <UserAdminPage/>,
                  },
                  {
                    path: "approvalFlow",
                    children: [
                      {
                        path: "",
                        element: <ApprovalFlowListPage/>,
                      },
                      {
                        path: ":id",
                        element: <ApprovalFlowEditPage/>,
                      },
                    ],
                  },
                ],
              },
              {
                path: "zyusetu",
                element: <HighlightFieldProvider/>, // これの配下でないとPDFViewerのハイライトが動かないです
                children: [
                  {
                    path: "tools",
                    children: [
                      {
                        path: "",
                        element: <ToolsPage/>,
                      },
                      {
                        path: "area-calculator",
                        element: <AreaCalculatorPage/>,
                      },
                      {
                        path: "bylaw-extractor",
                        element: <ManagementBylawExtractor/>
                      }
                    ]
                  },
                  {
                    path: "new",
                    children: [
                      {
                        path: "Sale",
                        element: <SaleNewZyusetuPage />
                      },
                      {
                        path: ":zyusetuType",
                        element: <RentNewZyusetuPage/>
                      }
                    ]
                  },
                  {
                    path: "reviews",
                    children: [
                      {
                        path: "",
                        element: <ZyusetuReviewListPage/>
                      },
                      {
                        path: "rejected",
                        element: <ZyusetuReviewListPage/>
                      },
                      {
                        path: "approved",
                        element: <ZyusetuReviewListPage/>
                      },
                      {
                        path: ":approvalStepExecutionId",
                        element: <ZyusetuReviewPage/>
                      }
                    ]
                  },
                  {
                    path: ":zyusetuId",
                    children: [
                      {
                        path: "edit",
                        element: <ZyusetuEditPage/>,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <NotFoundPage/>
      }
    ]
  },
]);

export function App() {
  const queryClient = new QueryClient({
    defaultOptions: {},
  });
  return (
    <Auth0Provider
      domain="auth0.paradis.ltd"
      clientId="qXlmzTmaj3quryaUbDZoy4BBkp75rWxF"
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: "ai-zyusetu",
      }}
      useRefreshTokensFallback={true}
      useCookiesForTransactions={true}
      onRedirectCallback={(appState) => {
        router.navigate(appState?.returnTo || "/");
      }}
    >
      <HelmetProvider>
        
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline/>
            <UserDataProvider>
              <RouterProvider router={router}/>
            </UserDataProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </Auth0Provider>
  );
}
